
import Seo from './Seo';

const TermsPage = () => {

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <Seo
        title="Terms of Service - Uumail"
        description="Review the terms that govern Uumail accounts, email alias forwarding, acceptable use, privacy, and service access."
        path="/terms"
      />
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">UUMail Terms of Service</h1>
          <p className="text-gray-600 mb-8">Effective Date: May 31, 2025</p>
          <p className="text-gray-700 mb-8">
            Welcome to UUMail! These Terms of Service ("Terms") govern your use of the UUMail website, application, and services (collectively, the "Service"), provided by UUMail ("we", "us", or "our"). By using our Service, you agree to be bound by these Terms.
          </p>
          <p className="text-gray-700 mb-8">
            If you do not agree to these Terms, please do not use the Service.
          </p>

          <div className="space-y-12">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Eligibility</h2>
              <p className="text-gray-700">
                You must be at least 13 years old (or the age of digital consent in your country) to use UUMail. If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Your Account</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                <li>You agree to provide accurate and complete information during registration and keep it updated.</li>
                <li>You are solely responsible for all activities under your account.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Acceptable Use</h2>
              <p className="text-gray-700 mb-4">You agree not to use the Service to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Violate any applicable laws or regulations;</li>
                <li>Send spam, phishing, or malicious content;</li>
                <li>Infringe on the intellectual property or privacy rights of others;</li>
                <li>Interfere with or disrupt the integrity or performance of the Service.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We reserve the right to suspend or terminate accounts that engage in prohibited behavior.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Email Handling and Storage</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>UUMail provides email forwarding and masking features.</li>
                <li>We do not store your email content unless you explicitly opt in to such a feature (e.g., cloud archiving).</li>
                <li>You retain full ownership and control over your emails.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Privacy</h2>
              <p className="text-gray-700">
                Your privacy is very important to us. Our collection and use of your information is governed by our Privacy Policy, which is incorporated into these Terms by reference.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
              <p className="text-gray-700">
                All rights, title, and interest in and to the Service (excluding your content) are and will remain the exclusive property of UUMail and its licensors. You may not copy, modify, or distribute any part of the Service without our prior written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Third-Party Services</h2>
              <p className="text-gray-700">
                The Service may rely on or link to third-party services or infrastructure. UUMail is not responsible for the content, policies, or practices of third-party services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Termination</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to suspend or terminate your account at any time, with or without notice, if:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>You violate these Terms;</li>
                <li>We are required to do so by law;</li>
                <li>We discontinue the Service (in which case, reasonable notice will be provided).</li>
              </ul>
              <p className="text-gray-700 mt-4">
                You may also terminate your account at any time through your account settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimers</h2>
              <p className="text-gray-700">
                The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the Service will be uninterrupted, error-free, or secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700">
                To the maximum extent permitted by law, UUMail and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, profits, or business opportunities.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify and hold harmless UUMail and its officers, directors, employees, and agents from any claims, liabilities, damages, and expenses arising from your use of the Service or your violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to These Terms</h2>
              <p className="text-gray-700">
                We may update these Terms from time to time. When we do, we will revise the "Effective Date" above and, if changes are material, notify you through the Service or by email. Continued use of the Service constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
              <p className="text-gray-700">
                These Terms are governed by and construed in accordance with the laws of Singapore (or your local jurisdiction, where applicable), without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact</h2>
              <p className="text-gray-700">
                If you have questions or concerns about these Terms, please contact us at:
              </p>
              <div className="mt-4">
                <p className="text-gray-700 font-medium">UUMail Legal Department</p>
                <a href="mailto:legal@uumail.com" className="text-blue-600 hover:text-blue-700">
                  Email: legal@uumail.com
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
